import { readActiveStorefrontSearchParams } from "~/app/router/activeStorefrontRouter";
import { Fragment, useEffect, useRef, useState } from "react";
import { type User } from "~/domains/navigation/types/NavigationTypes";
import SimpeRenderServerSideContent from "~/common/components/SimpeRenderServerSideContent";
import PageHeaderElement from "~/common/components/PageHeaderElement";
import DesktopPersonalNav from "~/features/navigation/components/headerMenu/DesktopPersonalNav";
import { userIsCustomer, userIsLoggedIn, userIsRegistered } from "~/domains/user/utils/roles";
import { MediasLogo } from "~/features/navigation/components/TopBar/MediasLogo";
import SearchBox from "~/features/search/components/Autosuggest/SearchBox";
import { CompanyLogo } from "~/domains/company/components/CompanyLogo";
import { getMediasVariant } from "~/features/navigation/components/TopBar/HeaderCircle";
import { UserNavigation } from "~/features/navigation/components/TopBar/UserNavigation";
import { HeaderLoginElement } from "~/features/navigation/components/TopBar/HeaderLoginElement";
import { Box, IconButton, styled } from "@schaeffler-commerce/design-system";
import { MobilePersonalNav } from "~/features/navigation/components/headerMenu/MobilePersonalNav";
import { useAffiliateTracking } from "~/domains/affiliate/hooks/useAffiliateTracking";
import { useOnMount } from "~/common/hooks/useOnMount";
import { ScrollToTop } from "~/common/components/ScrollToTop/ScrollToTop";
import { isDebugLanguageEnabled } from "~/utils/language/isDebugLanguageEnabled";
import { DebugLanguageToggle } from "~/common/components/DebugLanguageToggle";
import { CURRENT_LANGUAGE } from "~/app/config/appConstants";
import { getRequestBoot } from "~/app/config/requestBoot";
import { useAppConfig } from "~/app/config/useAppConfig";
import { SCAN_DMC_PAGE } from "~/utils/url/staticUrls";
import GlobalMessage from "~/features/navigation/components/GlobalMessage";
import { MergeCustomerCart } from "~/features/cart/components/MergeCustomerCart";
import classNames from "classnames";
import { isCheckoutPage, isDatasheetPage, isForkPage } from "~/utils/url/urlUtils";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useLogoutMessageHandler } from "~/features/auth/hooks/useLogoutMessageHandler";
import RedirectToPDPComponent from "~/bridges/header/Redirect/RedirectToPDPComponent";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { useTranslation } from "react-i18next";
import { ProductFruits } from "react-product-fruits";
import { Snackbar, SnackbarCloseButton } from "@schaeffler-commerce/design-system";
import { CategoryNavigation } from "~/features/categoryNavigation/components/CategoryNavigation";
import CheckoutHeader from "~/features/Checkout/components/CheckoutHeader";
import { LanguageSelector } from "~/features/categoryNavigation/components/LanguageSelector";
import QrCodeScannerOutlinedIcon from "@mui/icons-material/QrCodeScannerOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { ProductComparisonDrawerContainer } from "~/features/compareProducts/components/ProductComparisonDrawerContainer";
import { ComparisonDialogProvider } from "~/features/compareProducts/components/ComparisonDialogProvider";
import { useSession } from "~/app/providers/SessionProvider";
import { useOnboardingOverlay } from "~/features/registration/hooks/OnboardingOverlayContext";
import { OnboardingAuthenticationProvider } from "~/bridges/header/OnboardingAuthenticationProvider";
import { useCommerceContext, useSessionUser } from "~/app/config/useSession";
import { StaticPageHeader } from "~/features/navigation/components/StaticPageHeader/StaticPageHeader";
import { AuthOverlayHost } from "~/features/auth/AuthOverlayHost";
import { getRuntimeConfig } from "~/common/config/runtimeConfig";
import { SessionExpirePopupContainer } from "~/features/auth/session/useSessionExpirePopup";

export interface Props {
  user?: User;
  isSearchPage: boolean;
  showEdit: boolean;
  includeSessionExpirePopup?: boolean;
}

const useStickyNav = () => {
  const [windowPosition, setWindowPosition] = useState(0);

  useEffect(() => {
    if (!window.stickyNavigation) {
      return;
    }
    const listenScrollEvent = () => {
      setWindowPosition(window.scrollY);
    };
    window.addEventListener("scroll", listenScrollEvent);
    return () => window.removeEventListener("scroll", listenScrollEvent);
  }, []);

  if (windowPosition > 0) {
    return "stickyTop";
  }
};

/**
 *   The entire navigation on top of the page. Consists of three parts:
 *   * Top bar with search bar, company logo etc.
 *   * Middle navigation in HeaderMenu
 */
export const HeaderNavigation = ({ user, showEdit, isSearchPage }: Props) => {
  const { t } = useTranslation();
  const session = useSession();
  const sessionUser = useSessionUser();
  const selectedAccount = useCommerceContext()?.selectedAccount;
  const effectiveUser: User | undefined = user ?? (sessionUser ? { ...sessionUser, selectedAccount } : undefined);
  // Anonymous - logged out. Go public - logged out when go public is enabled.
  const loggedIn = userIsLoggedIn();
  const { openOnboarding } = useOnboardingOverlay();
  const mediasVariant = getMediasVariant();

  const screenSize = useNumericScreenSize();

  const isMinPhone = screenSize >= useNumericScreenSize.phone;
  const isMinTablet = screenSize >= useNumericScreenSize.tablet;
  const isMinDesktop = screenSize >= useNumericScreenSize.desktopS;

  const menuAnchorRef = useRef<HTMLDivElement | null>(null);

  const { val: topBar, setTrue: displayTopBar, setFalse: hideTopBar } = useBooleanState(true);

  const showLoginSnackbar = window.sessionStorage.getItem("accountChanged") === "true";
  const showDeleteAccountSuccessSnackbar = window.sessionStorage.getItem("accountDeleted") === "true";

  const { val: overlay, setTrue: showOverlay, setFalse: hideOverlay } = useBooleanState(false);
  const { val: recentSearches, setTrue: showRecentSearches, setFalse: hideRecentSearches } = useBooleanState(false);
  const { val: suggestions, setFalse: hideSuggestions, setTrue: showSuggestions } = useBooleanState(false);
  const {
    val: loginSnackbar,
    setFalse: hideLoginSnackbar,
    setTrue: openLoginSnackbar,
  } = useBooleanState(showLoginSnackbar);

  const { val: deleteAccountSuccessSnackbar, setFalse: hideDeleteAccountSuccessSnackbar } = useBooleanState(
    showDeleteAccountSuccessSnackbar,
  );

  useOnMount(() => {
    // Task 0231 slice 4: the onboarding overlay arrival markers read the
    // booted router's raw history location (one-shot like before).
    const params = readActiveStorefrontSearchParams();
    if (params?.get("onboarding") === "true") {
      const urlParamToken = params.get("token") ?? "";
      openOnboarding(urlParamToken);
    }
  });
  useEffect(() => {
    displayTopBar();
  }, [displayTopBar, isMinDesktop]);

  // Tracking in here, because this element is always created
  useAffiliateTracking();
  useLogoutMessageHandler();
  const showDebugLanguage = isDebugLanguageEnabled();

  const applyStickyNav = useStickyNav();

  const shouldAddProductFruits =
    (useAppConfig()?.configuration.productFruitsIntegrationEnabled ?? false) && userIsCustomer();
  const categoryNavigation = <CategoryNavigation navigationRef={menuAnchorRef} />;

  return (
    <>
      <ProductComparisonDrawerContainer />
      <GlobalMessage />
      <RedirectToPDPComponent />
      <MergeCustomerCart standardShellReady />
      <Snackbar
        message={t("accountSelection.accountSelectionSuccess")}
        open={loginSnackbar}
        onClose={() => {
          window.sessionStorage.setItem("accountChanged", "false");
          hideLoginSnackbar();
        }}
      />

      <Snackbar
        message={t("feature.deleteAccount.success")}
        open={deleteAccountSuccessSnackbar}
        onClose={() => {
          window.sessionStorage.setItem("accountDeleted", "false");
          hideDeleteAccountSuccessSnackbar();
        }}
        action={
          <SnackbarCloseButton
            onClick={() => {
              window.sessionStorage.setItem("accountDeleted", "false");
              hideDeleteAccountSuccessSnackbar();
            }}
          />
        }
      />

      {shouldAddProductFruits && (
        <ProductFruits
          workspaceCode="lfRfdsZ3tuukhX8r"
          language={(getRequestBoot().language || "en").substring(0, 2)}
          user={{
            username: effectiveUser?.uid ?? "",
            role: getUserRole(),
          }}
        />
      )}

      <div className={classNames(window.stickyNavigation && applyStickyNav)}>
        {overlay && isMinDesktop && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(0, 0, 0, 0.5)",
              zIndex: 1000,
            }}
          />
        )}
        {topBar && (
          <PageHeaderElement className={"scha__logo-header"}>
            {!showDebugLanguage && <MediasLogo variant={mediasVariant} href={true} />}
            {showDebugLanguage && <DebugLanguageToggle language={CURRENT_LANGUAGE} />}

            {isMinTablet && (
              <>
                {loggedIn && effectiveUser && !getRuntimeConfig().punchoutMode && (
                  <UserNavigation user={effectiveUser} showEdit={showEdit} />
                )}

                <LanguageSelector />
                {!loggedIn && <HeaderLoginElement />}

                {isMinTablet && isMinDesktop && (
                  <Box sx={{ flex: 1 }}>
                    <SearchBox
                      recentSearches={recentSearches}
                      showRecentSearches={showRecentSearches}
                      hideRecentSearches={hideRecentSearches}
                      suggestions={suggestions}
                      showSuggestions={showSuggestions}
                      hideSuggestions={hideSuggestions}
                      isSearchPage={isSearchPage}
                      overlay={overlay}
                      showOverlay={showOverlay}
                      hideOverlay={hideOverlay}
                    />
                  </Box>
                )}
              </>
            )}
            {/* removed becuase of CR SCHAEFF-36763 */}
            {/* {screenSize >= useNumericScreenSize.desktopS && <DesktopScanDMCTeaser />} */}

            <CompanyLogo
              style={{
                marginLeft: isMinTablet || isMinPhone ? "auto" : undefined,
                marginRight: isMinPhone ? 0 : undefined,
              }}
              className="scha__header__logo"
            />
          </PageHeaderElement>
        )}

        <PageHeaderElement ref={menuAnchorRef} className={"scha__header-menu"} zIndex={5}>
          {!isMinDesktop && (
            <>
              <MobileNavigationLeft className="scha__mobile-navigation__left">
                {!topBar && (
                  <IconButton color="primary" onClick={displayTopBar}>
                    <KeyboardArrowLeftIcon />
                  </IconButton>
                )}
                {isMinPhone && !isMinDesktop && topBar && categoryNavigation}
                <Box
                  sx={{
                    flex: 1,
                    width: { phone: "100%", "desktop-s": "auto" },
                    display: { phone: "flex", "desktop-s": "block" },
                    alignItems: { phone: "center", "desktop-s": "stretch" },
                  }}
                >
                  <SearchBox
                    recentSearches={recentSearches}
                    showRecentSearches={showRecentSearches}
                    hideRecentSearches={hideRecentSearches}
                    suggestions={suggestions}
                    showSuggestions={showSuggestions}
                    hideSuggestions={hideSuggestions}
                    topBar={topBar}
                    displayTopBar={displayTopBar}
                    hideTopBar={hideTopBar}
                    showOverlay={showOverlay}
                    hideOverlay={hideOverlay}
                    isSearchPage={isSearchPage}
                    overlay={overlay}
                  />
                </Box>
              </MobileNavigationLeft>

              {topBar && (
                <MobileNavigationRight className={"scha__mobile-navigation__right"}>
                  {isMinTablet && isMinPhone && <MobilePersonalNav />}
                  {isMinPhone && !isMinTablet && (
                    <Fragment>
                      {
                        <IconButton
                          className={classNames(
                            "scha__mobile-navigation__button scha__mobile-navigation__button--dmc",
                            userIsLoggedIn() && "scha__mobile-navigation__button--dmc--logged-in",
                          )}
                          color="secondary"
                          href={SCAN_DMC_PAGE}
                          ocs-tracking-aux-marker="access-icon"
                        >
                          <QrCodeScannerOutlinedIcon color="textSecondary" />
                        </IconButton>
                      }
                      {loggedIn && effectiveUser && <UserNavigation user={effectiveUser} showEdit={showEdit} />}
                      {!loggedIn && effectiveUser && <HeaderLoginElement />}
                    </Fragment>
                  )}
                </MobileNavigationRight>
              )}
            </>
          )}

          {isMinDesktop && (
            <>
              <div className="scha__header-menu__navigation">{categoryNavigation}</div>

              <div className="scha__header-menu__personal-nav">
                <DesktopPersonalNav />
              </div>
            </>
          )}
        </PageHeaderElement>

        {/* Tour is completely removed right now - see SCHAEFF-14767 */}

        <ScrollToTop />
        <AuthOverlayHost />
      </div>
    </>
  );
};

function LegacyStaticPageHeader() {
  return (
    <div>
      <StaticPageHeader />
      <SimpeRenderServerSideContent selector={"#BreadcrumbSlot"} />
    </div>
  );
}

export function createHeaderNavigation(props: Props, onLoginSuccess: () => void) {
  let header;
  if (isForkPage() || isDatasheetPage()) {
    header = <LegacyStaticPageHeader />;
  } else if (isCheckoutPage()) {
    header = <CheckoutHeader />;
  } else {
    header = (
      <ComparisonDialogProvider>
        <OnboardingAuthenticationProvider onLoginSuccess={onLoginSuccess}>
          <HeaderNavigation {...props} />
        </OnboardingAuthenticationProvider>
      </ComparisonDialogProvider>
    );
  }

  return (
    <>
      {props.includeSessionExpirePopup !== false && <SessionExpirePopupContainer />}
      {header}
    </>
  );
}

function getUserRole() {
  if (userIsCustomer()) {
    return "Business";
  }
  if (userIsRegistered()) {
    return "Plus";
  }
  return "Anonymous";
}

const MobileNavigationLeft = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  flex: 1,
  [theme.breakpoints.down("desktop-s")]: {
    gap: theme.spacing(1),
  },
}));

const MobileNavigationRight = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginLeft: "auto",
  gap: theme.spacing(1),
  ul: {
    paddingLeft: 0,
  },
}));
